import React from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, AlertTriangle, CheckCircle } from "lucide-react";
import SEO from "../components/SEO";

export const HMRCDeadlinesUK = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "HMRC Deadlines You Cannot Afford to Miss (UK 2026 Guide)",
    "description": "Complete guide to all HMRC deadlines for UK businesses in 2026. Self Assessment, VAT, Corporation Tax, PAYE deadlines with penalty costs.",
    "author": {
      "@type": "Organization",
      "name": "Brightson Accounting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Brightson Accounting"
    },
    "datePublished": "2026-04-06"
  };

  return (
    <>
      <SEO
        title="HMRC Deadlines UK 2026 | Tax Filing Dates | Brightson Accounting"
        description="All HMRC deadlines for 2026: Self Assessment 31 Jan, quarterly VAT, Corporation Tax 9 months after year-end. Avoid £100+ penalties with expert Wolverhampton accountants."
        canonical="/blog/hmrc-deadlines-uk"
        schema={articleSchema}
      />

      <article className="blog-article">
        <header className="article-header">
          <div className="container">
            <div className="article-meta">
              <span className="article-category">
                <Calendar size={16} />
                Staying Compliant
              </span>
              <span className="article-date">6 April 2026</span>
              <span className="article-read-time">6 min read</span>
            </div>

            <h1>HMRC Deadlines You Cannot Afford to Miss (UK 2026 Guide)</h1>

            <p className="article-subtitle">
              Complete calendar of HMRC filing and payment deadlines — miss these and you'll pay £100+ in penalties
            </p>

            <div className="article-author">
              <div className="author-info">
                <strong>Brightson Accounting (FCCA Qualified)</strong>
                <span>Tax Compliance Specialists | Wolverhampton & West Midlands</span>
              </div>
            </div>
          </div>
        </header>

        <section className="article-content">
          <div className="container">
            <div className="article-body">
              
              <p className="article-lead">
                Many small businesses in Birmingham, Wolverhampton, and across the West Midlands get penalised simply due to missed deadlines. HMRC doesn't send reminders — it's your responsibility to file and pay on time.
              </p>

              <p>
                Missing a deadline costs a minimum of £100 immediately, plus interest on any unpaid tax. For many businesses, it's avoidable stress and wasted money.
              </p>

              <div className="info-box" style={{ background: '#fff3cd', borderLeft: '4px solid #ffc107' }}>
                <strong>⚠️ Quick Summary</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li><strong>31 January:</strong> Self Assessment deadline (file + pay)</li>
                  <li><strong>Quarterly:</strong> VAT returns (1 month + 7 days after period end)</li>
                  <li><strong>9 months after year-end:</strong> Corporation Tax payment</li>
                  <li><strong>19th of every month:</strong> PAYE payment (if you have employees)</li>
                  <li>Miss any of these = £100+ penalty + interest</li>
                </ul>
              </div>

              <h2>Self Assessment Tax Return Deadlines</h2>

              <p>
                If you're self-employed, a director, a landlord, or have income over £100,000, you must file a Self Assessment tax return.
              </p>

              <h3>Key Dates:</h3>

              <table style={{ width: '100%', marginTop: '1rem', marginBottom: '2rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Deadline</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Penalty if Late</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}><strong>5 October 2026</strong></td>
                    <td style={{ padding: '0.75rem' }}>Register for Self Assessment (if new)</td>
                    <td style={{ padding: '0.75rem' }}>£100+</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}><strong>31 October 2026</strong></td>
                    <td style={{ padding: '0.75rem' }}>Paper return deadline</td>
                    <td style={{ padding: '0.75rem' }}>£100</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}><strong>31 January 2027</strong></td>
                    <td style={{ padding: '0.75rem' }}>Online return + payment deadline</td>
                    <td style={{ padding: '0.75rem' }}>£100 + daily penalties</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem' }}><strong>31 July 2027</strong></td>
                    <td style={{ padding: '0.75rem' }}>Second payment on account</td>
                    <td style={{ padding: '0.75rem' }}>Interest charges</td>
                  </tr>
                </tbody>
              </table>

              <p>
                <strong>What is "payment on account"?</strong>
              </p>

              <p>
                If you owe over £1,000 in tax, HMRC requires you to pay tax in advance for the next year. You make two payments:
              </p>

              <ul>
                <li>31 January: First payment (50% of last year's tax bill)</li>
                <li>31 July: Second payment (remaining 50%)</li>
                <li>31 January (following year): Balancing payment + first payment for new year</li>
              </ul>

              <p>
                Full guide: <Link to="/blog/self-assessment-compliance">Self Assessment Compliance: Avoid Costly Errors</Link>
              </p>

              {/* Mid-Article CTA */}
              <div className="article-cta-box" style={{ background: '#f8f9fa', border: '2px solid #0066cc', marginTop: '2rem', marginBottom: '2rem' }}>
                <h3>💡 Want to see how much tax you could save?</h3>
                <p>
                  Most UK business owners are overpaying without realising it.
                </p>
                <Link to="/corporation-tax-calculator" className="article-cta-button">
                  👉 Try the Corporation Tax Calculator
                </Link>
              </div>

              <h2>VAT Return Deadlines</h2>

              <p>
                If you're VAT registered, you must submit returns quarterly (or monthly if you've opted in).
              </p>

              <h3>Standard VAT Quarters (Most Common):</h3>

              <table style={{ width: '100%', marginTop: '1rem', marginBottom: '2rem', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #d1d5db' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Quarter Ending</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left' }}>Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>31 March</td>
                    <td style={{ padding: '0.75rem' }}>7 May</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>30 June</td>
                    <td style={{ padding: '0.75rem' }}>7 August</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '0.75rem' }}>30 September</td>
                    <td style={{ padding: '0.75rem' }}>7 November</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '0.75rem' }}>31 December</td>
                    <td style={{ padding: '0.75rem' }}>7 February (following year)</td>
                  </tr>
                </tbody>
              </table>

              <p>
                <strong>Important:</strong> VAT deadlines are 1 month + 7 days after the end of your VAT period.
              </p>

              <p>
                All VAT returns must be submitted electronically via Making Tax Digital (MTD) for VAT. Paper submissions are no longer accepted.
              </p>

              <p>
                Full guide: <Link to="/blog/vat-compliance-mistakes">VAT Compliance: Common Mistakes That Cost Businesses Money</Link>
              </p>

              <h2>Corporation Tax Deadlines</h2>

              <p>
                If you run a limited company, you must file a Corporation Tax return (CT600) and pay Corporation Tax.
              </p>

              <h3>Key Deadlines:</h3>

              <ul>
                <li><strong>9 months after year-end:</strong> Corporation Tax payment deadline</li>
                <li><strong>12 months after year-end:</strong> CT600 return filing deadline</li>
              </ul>

              <p>
                <strong>Example:</strong>
              </p>
              <ul>
                <li>Company year-end: 31 March 2026</li>
                <li>Payment deadline: 1 January 2027 (9 months later)</li>
                <li>Filing deadline: 31 March 2027 (12 months later)</li>
              </ul>

              <p className="highlight-note">
                Pro Tip: Pay Corporation Tax early if you can. Interest rates on late payments are high (currently 7.75% per year).
              </p>

              <p>
                Want to reduce your Corporation Tax bill legally? <Link to="/corporation-tax-calculator">Use our free calculator</Link> to estimate potential savings.
              </p>

              <p>
                Full guide: <Link to="/blog/corporation-tax-compliance">Corporation Tax Compliance Explained (UK 2026)</Link>
              </p>

              <h2>PAYE & Payroll Deadlines</h2>

              <p>
                If you employ staff (including yourself as a director taking a salary), you must operate PAYE.
              </p>

              <h3>Monthly Deadlines:</h3>

              <ul>
                <li><strong>19th of every month:</strong> PAYE payment deadline (by post)</li>
                <li><strong>22nd of every month:</strong> PAYE payment deadline (electronic payment)</li>
                <li><strong>Before/on payday:</strong> Submit Full Payment Submission (FPS) to HMRC</li>
              </ul>

              <h3>Annual Deadlines:</h3>

              <ul>
                <li><strong>19 May:</strong> P60s issued to all employees (for previous tax year)</li>
                <li><strong>6 July:</strong> P11D forms submitted to HMRC (for benefits in kind)</li>
                <li><strong>19 July:</strong> Class 1A National Insurance payment (on P11D benefits)</li>
              </ul>

              <p>
                Most West Midlands businesses use payroll software to automate these submissions. If you're still doing payroll manually, you're at high risk of missing deadlines.
              </p>

              <h2>Making Tax Digital (MTD) Deadlines</h2>

              <p>
                From April 2026, Making Tax Digital for Income Tax becomes mandatory for sole traders and landlords with income over £50,000.
              </p>

              <h3>MTD Quarterly Deadlines:</h3>

              <p>
                You must submit quarterly updates to HMRC:
              </p>

              <ul>
                <li>Quarter 1 (6 Apr - 5 Jul): Deadline 5 August</li>
                <li>Quarter 2 (6 Jul - 5 Oct): Deadline 5 November</li>
                <li>Quarter 3 (6 Oct - 5 Jan): Deadline 5 February</li>
                <li>Quarter 4 (6 Jan - 5 Apr): Deadline 5 May</li>
              </ul>

              <p>
                Plus a final declaration by 31 January following the tax year end.
              </p>

              <p>
                Full guide: <Link to="/blog/making-tax-digital-compliance">Making Tax Digital: What You Must Do Before April 2026</Link>
              </p>

              <h2>Penalty Costs (What Happens If You're Late)</h2>

              <div className="info-box" style={{ background: '#fee', borderLeft: '4px solid #dc3545' }}>
                <strong>⚠️ Late Filing Penalties (Self Assessment & Corporation Tax)</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li><strong>1 day late:</strong> £100 fixed penalty</li>
                  <li><strong>3 months late:</strong> Additional £10 per day (max £900)</li>
                  <li><strong>6 months late:</strong> £300 or 5% of tax due (whichever is higher)</li>
                  <li><strong>12 months late:</strong> Another £300 or 5% of tax due</li>
                </ul>
              </div>

              <div className="info-box" style={{ background: '#fee', borderLeft: '4px solid #dc3545', marginTop: '1rem' }}>
                <strong>⚠️ Late Payment Penalties</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li><strong>30 days late:</strong> 5% of unpaid tax</li>
                  <li><strong>6 months late:</strong> Additional 5% of unpaid tax</li>
                  <li><strong>12 months late:</strong> Another 5% of unpaid tax</li>
                  <li><strong>Plus:</strong> Interest charged from deadline (currently 7.75% per year)</li>
                </ul>
              </div>

              <p>
                Example: Miss the 31 January Self Assessment deadline by 4 months with £5,000 tax due:
              </p>

              <ul>
                <li>£100 fixed penalty</li>
                <li>£900 daily penalties (£10 x 90 days)</li>
                <li>£250 interest (approx, 4 months at 7.75%)</li>
                <li><strong>Total cost:</strong> £1,250 in penalties + interest</li>
              </ul>

              <p>
                This is money wasted. For a fraction of that cost, you could work with an accountant who ensures you never miss a deadline.
              </p>

              <p>
                Full guide: <Link to="/blog/hmrc-penalties-guide">HMRC Penalties Explained: How to Avoid Them</Link>
              </p>

              <h2>How to Never Miss a Deadline</h2>

              <h3>Option 1: Calendar Reminders</h3>

              <p>
                Set recurring calendar reminders 2 weeks before every deadline:
              </p>

              <ul>
                <li>15 January: Self Assessment reminder</li>
                <li>7th of month after VAT quarter: VAT reminder</li>
                <li>3 months before year-end + 9 months: Corporation Tax payment reminder</li>
              </ul>

              <h3>Option 2: Use Accounting Software</h3>

              <p>
                Software like Xero, QuickBooks, or FreeAgent tracks deadlines for you and sends automatic reminders.
              </p>

              <h3>Option 3: Work with an Accountant</h3>

              <p>
                At Brightson Accounting, we track all deadlines for our clients. You don't have to remember dates — we handle filing and remind you when payments are due.
              </p>

              <p>
                Most of our Wolverhampton and West Midlands clients never think about deadlines because we manage compliance for them.
              </p>

              <h2>Deadline Checklist (Print This)</h2>

              <div className="info-box" style={{ background: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                <strong>✅ 2026 Tax Year Deadlines</strong>
                <ul style={{ marginBottom: 0 }}>
                  <li>5 Oct 2026: Register for Self Assessment (if new)</li>
                  <li>31 Oct 2026: Paper Self Assessment deadline</li>
                  <li>31 Jan 2027: Online Self Assessment + payment</li>
                  <li>Quarterly: VAT returns (1 month + 7 days after quarter)</li>
                  <li>19th monthly: PAYE payment</li>
                  <li>9 months after year-end: Corporation Tax payment</li>
                  <li>12 months after year-end: CT600 filing</li>
                </ul>
              </div>

              <p>
                Want help staying on top of these deadlines? <Link to="/contact">Speak to an accountant</Link> who specialises in compliance.
              </p>

              <h2>Final Thoughts</h2>

              <p>
                HMRC deadlines are not negotiable. They don't send reminders. And penalties add up fast.
              </p>

              <p>
                The good news? With basic organisation (or an accountant), you'll never miss a deadline.
              </p>

              <p>
                The businesses that get penalised are those who:
              </p>

              <ul>
                <li>Leave everything until the last minute</li>
                <li>Don't track deadlines</li>
                <li>Try to manage compliance alone without systems</li>
              </ul>

              <p>
                Don't be one of them.
              </p>

            </div>

            {/* End CTA */}
            <div className="article-cta-box" style={{ marginTop: '3rem' }}>
              <h2>🚀 Ready to stay compliant and reduce your tax bill?</h2>
              <p>
                We help businesses across Wolverhampton and the West Midlands:
              </p>
              <ul>
                <li>Stay compliant with HMRC</li>
                <li>Reduce tax legally</li>
                <li>Avoid penalties and investigations</li>
              </ul>
              <Link to="/contact" className="article-cta-button">
                👉 Book a Free Consultation
              </Link>
            </div>

            {/* Related Articles */}
            <div className="related-articles" style={{ marginTop: '3rem' }}>
              <h3>Related Guides</h3>
              <div className="related-links">
                <Link to="/blog/hmrc-compliance-guide-uk" className="related-link">HMRC Compliance Guide</Link>
                <Link to="/blog/hmrc-penalties-guide" className="related-link">HMRC Penalties Explained</Link>
                <Link to="/blog/self-assessment-compliance" className="related-link">Self Assessment Compliance</Link>
                <Link to="/contact" className="related-link">Contact</Link>
              </div>
            </div>
          </div>
        </section>
      </article>
    </>
  );
};

export default HMRCDeadlinesUK;
